import { useMemo } from 'react';

import { useProfilesData } from '@/features/user/api';

import { isApexProduct } from './is-apex-product';

export type ModuleNoun = {
  singular: string;
  plural: string;
};

/**
 * The one and only place the "Module"/"Chapter" words live. Every component
 * reads these through `useModuleNoun()` so the wording is driven by a single
 * source of truth keyed on the selected course's product.
 */
export const MODULE_NOUN: ModuleNoun = {
  singular: 'Module',
  plural: 'Modules',
};
export const APEX_NOUN: ModuleNoun = {
  singular: 'Chapter',
  plural: 'Chapters',
};

/** Pure mapping from a product name to its noun. */
export const getModuleNoun = (productName?: string): ModuleNoun =>
  isApexProduct(productName) ? APEX_NOUN : MODULE_NOUN;

/**
 * Converts any default "Module"/"Modules" wording inside a string to the given
 * course noun, leaving unrelated text untouched (e.g. the online "Recordings"
 * tab). Handles both the Title-case noun ("Module") and the lowercase form used
 * mid-sentence ("...completing the module"). This is the single place the swap
 * rule lives, so callers only pass the default copy and the noun.
 */
export const convertModuleNoun = (text: string, noun: ModuleNoun): string => {
  if (noun.plural === MODULE_NOUN.plural) {
    return text;
  }
  // Plural before singular so "Modules" isn't partially matched by the singular
  // rule; Title-case before lowercase for the same reason within each number.
  return text
    .replaceAll(/\bModules\b/g, noun.plural)
    .replaceAll(/\bModule\b/g, noun.singular)
    .replaceAll(/\bmodules\b/g, noun.plural.toLowerCase())
    .replaceAll(/\bmodule\b/g, noun.singular.toLowerCase());
};

/**
 * Resolves the noun for the currently selected course. Mirrors the existing
 * product-driven hooks (e.g. useDashboardComponents) by reading the selected
 * course internally, memoising on its product name, and delegating to the pure
 * getModuleNoun mapping.
 */
export const useModuleNoun = (): ModuleNoun => {
  const { selectedCourse } = useProfilesData();
  const productName = selectedCourse?.product_name;
  return useMemo(() => getModuleNoun(productName), [productName]);
};
